import * as LZString from 'lz-string';

import { Compression, Encoding, ICompressedData } from '@repo/types-client';

/**
 * Compresses a JSON object into a string using the specified encoding and compression method.
 * @function compress
 * @template T - The type of the JSON object.
 * @param {T} json - The JSON object to compress.
 * @param {object} options - The options for compression.
 * @param {Encoding} options.encoding - The encoding method to use ('base64').
 * @param {Compression} options.compression - The compression method to use ('lz77').
 * @returns {ICompressedData<string>} The compressed data.
 */
const compress = <T = unknown>(
  json: T,
  options: {
    encoding: Encoding;
    compression: Compression;
  },
): ICompressedData<string> => {
  const data = JSON.stringify(json);

  let compressed: string | undefined;

  if (options.encoding === 'base64') {
    if (!options.compression) {
      compressed = btoa(unescape(encodeURIComponent(data)));
    }

    if (options.compression === 'lz77') {
      compressed = LZString.compressToBase64(data);
    }
  }

  return {
    encoding: options.encoding,
    compression: options.compression,
    data: compressed!,
  };
};

/**
 * Decompresses a string into a JSON object using the specified encoding and compression method.
 * @function decompress
 * @template T - The type of the JSON object.
 * @param {ICompressedData<string> | undefined} compressed - The compressed data to decompress.
 * @returns {T | undefined} The decompressed JSON object.
 */
const decompress = <T = unknown>(
  compressed: ICompressedData<string> | undefined,
): T | undefined => {
  if (!compressed?.data) return undefined;

  let decompressed: string | undefined;

  if (compressed.encoding === 'base64') {
    if (!compressed.compression) {
      decompressed = decodeURIComponent(escape(atob(compressed.data)));
    }

    if (compressed.compression === 'lz77') {
      decompressed = LZString.decompressFromBase64(compressed.data);
    }
  }

  if (!decompressed) return undefined;

  return JSON.parse(decompressed);
};

export { decompress, compress };
